/**
 * useSubscription Hook
 *
 * Manages subscription state for the current user.
 * Provides real-time updates and access checking.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth, useFirebaseAuth } from '@/lib/firebase';
import {
  subscriptionsService,
  type Subscription,
  type SubscriptionStatus,
} from '@/lib/firebase/subscriptions';

interface SubscriptionAccess {
  hasAccess: boolean;
  status: SubscriptionStatus | 'none';
  daysRemaining: number;
  isTrialEnding: boolean;
  isExpiringSoon: boolean;
  expirationDate: Date | null;
  canAccessPortal: boolean;
}

interface UseSubscriptionReturn {
  subscription: Subscription | null;
  access: SubscriptionAccess;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

export function useSubscription(): UseSubscriptionReturn {
  const { firebaseUser } = useAuth();
  const { isInitialized } = useFirebaseAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    // Don't set isLoading=false until auth is initialized
    if (!isInitialized) {
      return;
    }

    if (!firebaseUser?.uid) {
      setSubscription(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const sub = await subscriptionsService.get(firebaseUser.uid);
      setSubscription(sub);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load subscription'));
    } finally {
      setIsLoading(false);
    }
  }, [firebaseUser?.uid, isInitialized]);

  // Subscribe to real-time updates
  useEffect(() => {
    // Wait for auth to initialize before deciding subscription state
    if (!isInitialized) {
      return;
    }

    if (!firebaseUser?.uid) {
      setSubscription(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const unsubscribe = subscriptionsService.subscribe(firebaseUser.uid, (sub) => {
      setSubscription(sub);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [firebaseUser?.uid, isInitialized]);

  // Calculate access based on current subscription state
  const access = subscriptionsService.checkAccess(subscription);

  return {
    subscription,
    access,
    isLoading,
    error,
    refresh,
  };
}
